"use client";

import * as React from "react";

import { cn } from "../../lib/utils";
import { Badge, type BadgeColor, type BadgeElement } from "../ui/badge";

export type StatusBadgeProps = {
	/** The resolved human label. Never an enum value. */
	label: string;
	variant?: BadgeColor;
	/** Leading dot, which is how a status reads apart from a category label. */
	dot?: boolean;
	/**
	 * Element to render. `span` here, unlike `Badge`'s own `div` default, because a
	 * status pill is phrasing content: an answer renders one mid-sentence, inside
	 * the `<p>` markdown puts prose in, and a `<div>` there closes the paragraph
	 * early — the badge drops onto its own line and React logs
	 * "<div> cannot be a descendant of <p>". A `span` is valid in both places, and
	 * `inline-flex` means it looks the same in either.
	 */
	as?: BadgeElement;
	className?: string;
};

/**
 * A status, as a pill.
 *
 * Deliberately domain-free: it takes a resolved label and variant, never an enum
 * name. Mapping an enum to a label is the host's business — the label has to come
 * from the schema, and the helper that logs unknown values lives in the app.
 */
export const StatusBadge = ({
	label,
	variant = "secondary",
	dot = true,
	as = "span",
	className,
}: StatusBadgeProps) => (
	<Badge
		as={as}
		data-testid="status-badge"
		// `h-5 px-2` beats the pill shape's own `h-4 px-1`, which sits too tight around an
		// uppercase label and its dot. Ahead of `className` so a caller can still override.
		// `max-w-full` is the safety valve, not the normal case: the pill sizes to its
		// label and only ever clips when a container refuses it the room. Without it a
		// narrow cell has nothing to stop the badge spilling past its own edge.
		className={cn(
			"h-5 max-w-full gap-1.5 px-2 align-middle text-[0.6875rem] font-semibold uppercase tracking-wide",
			className,
		)}
		shape="pill"
		variant={variant}
	>
		{dot && (
			<span
				aria-hidden
				className="size-1.5 shrink-0 rounded-full bg-current"
				data-testid="status-badge-dot"
			/>
		)}
		{/*
		 * Never wrapped, clipped when it has to be.
		 *
		 * A two-word status in a narrow table column - "IN PROGRESS" beside a Priority
		 * and an Assignee - used to break onto a second line, and the pill is a fixed
		 * `h-5`: the second line rendered outside it. Agent answers put these in
		 * markdown tables, which size columns to content, so the narrow case is the
		 * common one rather than the edge.
		 *
		 * On the label rather than the badge because the badge is a flex container,
		 * where `text-overflow` has nothing to act on, and `min-w-0` because a flex
		 * item will not shrink below its content without it.
		 */}
		<span className="min-w-0 truncate">{label}</span>
	</Badge>
);
